import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Store, Plus } from "lucide-react";
import { sharedApi } from "@/lib/api";
import { toast } from "sonner";
import { getActiveStoreId, setActiveStoreId } from "@/lib/activeStore";
import { STORE_CATEGORIES } from "@/components/partner/SocialCategoryCard";

/**
 * Iter-412 §3 — multi-store: switcher chips + Add-another-store card.
 *   GET  /vendors/me/stores → {stores:[{id, business_name, store_code,
 *        kyc_status, pos_status, kyc_inherited_from, …}]} oldest-first
 *   POST /vendors/me/stores {trading_name*, physical_address*, …,
 *        same_company:true|false [, legal_business_name, owner_name,
 *        business_email, nzbn]}
 * Switching a store sets the device-persisted X-Store-Id (injected by
 * the sharedApi interceptor) and refreshes every vendors/me* surface.
 */

const OPTS = {
  headers: { "X-App-Platform": "nz_classified" },
  params: { platform: "nz_classified" },
};

export default function StoresPanel() {
  const qc = useQueryClient();
  const [activeId, setActiveId] = useState(getActiveStoreId());

  const storesQ = useQuery({
    queryKey: ["vendor-stores"],
    retry: false,
    queryFn: async () =>
      (await sharedApi.get("/classifieds/engagement/vendors/me/stores", OPTS)).data,
  });

  if (storesQ.isLoading || storesQ.isError) return null;
  const stores = storesQ.data?.stores || [];
  if (stores.length === 0) return null;

  const current = stores.find((st) => st.id === activeId) || stores[0];
  const anyApproved = stores.some((st) => st.kyc_status === "approved");

  const switchTo = (id) => {
    setActiveStoreId(id);
    setActiveId(id);
    // Every vendors/me* surface re-fetches under the new X-Store-Id.
    qc.invalidateQueries({ queryKey: ["vendor-me"] });
    qc.invalidateQueries({ queryKey: ["vendor-stats"] });
  };

  return (
    <>
      {stores.length > 1 && (
        <div className="mt-6 rounded-2xl bg-white shadow-card p-5" data-testid="store-switcher">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-slate-500">
            <Store className="w-4 h-4" /> 🏬 Your stores
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {stores.map((st) => (
              <button
                key={st.id}
                type="button"
                data-testid={`store-chip-${st.id}`}
                onClick={() => st.id !== current.id && switchTo(st.id)}
                className={`rounded-full border px-3.5 py-1.5 text-xs font-bold transition-colors ${
                  st.id === current.id
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                }`}
              >
                {st.business_name} {st.kyc_status === "approved" ? "✅" : "⏳"}
              </button>
            ))}
          </div>
        </div>
      )}
      {anyApproved && (
        <AddStoreCard
          nzbn={current?.nzbn || storesQ.data?.nzbn}
          onCreated={(store) => {
            qc.invalidateQueries({ queryKey: ["vendor-stores"] });
            if (store?.id) switchTo(store.id);
          }}
        />
      )}
    </>
  );
}

const EMPTY = {
  trading_name: "", physical_address: "", business_phone: "", store_category: "",
  discount_percent: "", fee_mode: "", legal_business_name: "", owner_name: "",
  business_email: "", nzbn: "",
};

function AddStoreCard({ nzbn, onCreated }) {
  const [open, setOpen] = useState(false);
  const [same, setSame] = useState(true);
  const [f, setF] = useState(EMPTY);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const createMut = useMutation({
    mutationFn: async () => {
      const body = {
        trading_name: f.trading_name.trim(),
        physical_address: f.physical_address.trim(),
        same_company: same,
      };
      if (f.business_phone.trim()) body.business_phone = f.business_phone.trim();
      if (f.store_category) body.store_category = f.store_category;
      if (f.discount_percent !== "") body.discount_percent = Number(f.discount_percent);
      if (f.fee_mode) body.fee_mode = f.fee_mode;
      if (!same) {
        body.legal_business_name = f.legal_business_name.trim();
        body.owner_name = f.owner_name.trim();
        body.business_email = f.business_email.trim();
        if (f.nzbn.trim()) body.nzbn = f.nzbn.trim();
      }
      return (await sharedApi.post("/classifieds/engagement/vendors/me/stores", body, OPTS)).data;
    },
    onSuccess: (data) => {
      toast.success(
        data?.kyc_status === "approved"
          ? "Store created — KYC carried over, it's ready to go."
          : "Store created — it needs its own KYC approval before going live.",
      );
      setF(EMPTY);
      setOpen(false);
      onCreated(data);
    },
    onError: (err) => {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't create the store — please try again.");
    },
  });

  if (!open) {
    return (
      <div className="mt-6">
        <button
          type="button"
          data-testid="add-store-open"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-2 rounded-full bg-white hover:bg-slate-50 text-slate-800 border border-slate-200 text-sm font-bold px-5 py-2.5 shadow-sm transition-colors"
        >
          <Plus className="w-4 h-4" /> Add another store
        </button>
      </div>
    );
  }

  return (
    <div className="mt-6 rounded-2xl bg-white shadow-card p-5 md:p-6" data-testid="add-store-card">
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-slate-500">
        <Plus className="w-4 h-4" /> Add another store
      </div>

      <div className="mt-4 flex gap-2">
        <ToggleChip active={same} onClick={() => setSame(true)} testid="add-store-same">
          Same company
        </ToggleChip>
        <ToggleChip active={!same} onClick={() => setSame(false)} testid="add-store-different">
          Different company
        </ToggleChip>
      </div>
      <p className="mt-2 text-xs text-slate-500" data-testid="add-store-entity-copy">
        {same
          ? `Same legal entity${nzbn ? ` (NZBN ${nzbn})` : ""} — KYC and payouts carry over.`
          : "Different legal entity — this store needs its own KYC approval and its own Stripe payout account."}
      </p>

      <div className="mt-4 grid sm:grid-cols-2 gap-3">
        <input data-testid="add-store-trading-name" value={f.trading_name} onChange={set("trading_name")} placeholder="Trading name *" className="pp-input" />
        <input data-testid="add-store-address" value={f.physical_address} onChange={set("physical_address")} placeholder="Physical address *" className="pp-input" />
        <input data-testid="add-store-phone" value={f.business_phone} onChange={set("business_phone")} placeholder="Business phone" className="pp-input" />
        <select data-testid="add-store-category" value={f.store_category} onChange={set("store_category")} className="pp-input">
          <option value="">Store category</option>
          {STORE_CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <input data-testid="add-store-discount" value={f.discount_percent} onChange={set("discount_percent")} placeholder="Discount % (e.g. 10)" inputMode="numeric" className="pp-input" />
        <select data-testid="add-store-fee-mode" value={f.fee_mode} onChange={set("fee_mode")} className="pp-input">
          <option value="">Fee mode</option>
          <option value="customer_pays">Customer pays the Service fee</option>
          <option value="vendor_absorbs">Store absorbs the card cost</option>
        </select>
        {!same && (
          <>
            <input data-testid="add-store-legal-name" value={f.legal_business_name} onChange={set("legal_business_name")} placeholder="Legal business name *" className="pp-input" />
            <input data-testid="add-store-owner" value={f.owner_name} onChange={set("owner_name")} placeholder="Owner name *" className="pp-input" />
            <input data-testid="add-store-email" value={f.business_email} onChange={set("business_email")} placeholder="Business email *" inputMode="email" className="pp-input" />
            <input data-testid="add-store-nzbn" value={f.nzbn} onChange={set("nzbn")} placeholder="NZBN" inputMode="numeric" className="pp-input" />
          </>
        )}
      </div>

      <div className="mt-4 flex items-center gap-3">
        <button
          type="button"
          data-testid="add-store-submit"
          disabled={createMut.isPending || !f.trading_name.trim() || !f.physical_address.trim()}
          onClick={() => createMut.mutate()}
          className="inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white text-sm font-bold px-6 py-2.5 shadow-card transition-colors disabled:opacity-60"
        >
          {createMut.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
          {same ? "Create store (KYC inherited)" : "Create store (needs KYC approval)"}
        </button>
        <button
          type="button"
          data-testid="add-store-cancel"
          onClick={() => setOpen(false)}
          className="text-xs font-bold text-slate-500 hover:text-slate-800 underline"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function ToggleChip({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`rounded-full border px-4 py-1.5 text-xs font-bold transition-colors ${
        active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
      }`}
    >
      {children}
    </button>
  );
}
